<template>

  <div v-if="showSnmpForm" class="mb-4">
    <SNMPView
      :target="editingSnmpTarget"
      :device-id="route.params.id"
      @saved="handleSnmpTargetSaved"
      @cancel="cancelSnmpEdit"
    />
  </div>


  <div class="p-6">
    <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-6 flex items-center">
      <button
        @click="$router.push('/device')"
        class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4 mr-1"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
            clip-rule="evenodd"
          />
        </svg>
        Back to Devices
      </button>
      Device Configuration
    </h1>

    <div v-if="loading" class="text-center py-4">
      <svg
        class="animate-spin h-8 w-8 mr-2 inline-block dark:text-gray-50 text-gray-900"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z"
        ></path>
      </svg>
    </div>

    <div
      v-else-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <form v-else @submit.prevent="saveConfiguration" class="space-y-6 p-6 glass-card">
      <!-- Device Information -->
      <h3 class="text-blue-600 dark:text-blue-400 font-semibold mb-4 text-sm uppercase tracking-wider">
        Device Information
      </h3>

      <div class="grid grid-cols-2 gap-6">
        <div>
          <label for="device_name" class="block text-sm font-medium text-gray-700 dark:text-white">
            Device Name
          </label>
          <input
            id="device_name"
            v-model="config.name"
            type="text"
            class="mt-1 block w-full px-3 py-2 text-base border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm rounded-md"
          />
        </div>

        <div>
          <label for="location" class="block text-sm font-medium text-gray-700 dark:text-white">
            Location
          </label>
          <input
            id="location"
            v-model="config.location"
            type="text"
            class="mt-1 block w-full px-3 py-2 text-base border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm rounded-md"
          />
        </div>

        <!-- Ping Test -->
        <div>
          <div class="flex items-center mb-4">
            <input
              id="ping_test"
              v-model="config.tests.ping.enabled"
              type="checkbox"
              class="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label for="ping_test" class="ml-3 text-base font-medium text-gray-900 dark:text-white">
              PING TEST
            </label>
          </div>

          <div class="ml-8 space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                Targets
              </label>
              <div class="space-y-2">
                <div
                  v-for="(target, index) in config.tests.ping.targets"
                  :key="index"
                  class="flex items-center gap-2"
                >
                  <input
                    v-model="config.tests.ping.targets[index]"
                    type="text"
                    class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    @click="removeTarget('ping', index)"
                    class="text-red-500 hover:text-red-700 p-2 hover:bg-transparent bg-transparent"
                  >
                    <Trash/>
                  </button>
                </div>
              </div>
              <button
                type="button"
                @click="addTarget('ping')"
                class="mt-2 btn text-blue-300 dark:text-blue-400 hover:text-blue-700 text-sm flex items-center gap-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
                Add Target
              </button>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                Interval (seconds)
              </label>
              <input
                v-model.number="config.tests.ping.interval_sec"
                type="number"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <!-- DNS Test -->
        <div>
          <div class="flex items-center mb-4">
            <input
              id="dns_test"
              v-model="config.tests.dns.enabled"
              type="checkbox"
              class="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label for="dns_test" class="ml-3 text-base font-medium text-gray-900 dark:text-white">
              DNS Test
            </label>
          </div>

          <div class="ml-8 space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                Targets
              </label>
              <div class="space-y-2">
                <div
                  v-for="(target, index) in config.tests.dns.targets"
                  :key="index"
                  class="flex items-center gap-2"
                >
                  <input
                    v-model="config.tests.dns.targets[index]"
                    type="text"
                    class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    @click="removeTarget('dns', index)"
                    class="text-red-500 hover:text-red-700 hover:bg-transparent bg-transparent p-2"
                  >
                    <Trash/>
                  </button>
                </div>
              </div>
              <button
                type="button"
                @click="addTarget('dns')"
                class="mt-2 btn text-blue-300 dark:text-blue-400 hover:text-blue-700 text-sm flex items-center gap-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
                Add Target
              </button>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                Interval (seconds)
              </label>
              <input
                v-model.number="config.tests.dns.interval_sec"
                type="number"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <!-- TCP Handshake Test -->
        <div>
          <div class="flex items-center mb-4">
            <input
              id="tcp_test"
              v-model="config.tests.tcp_handshake.enabled"
              type="checkbox"
              class="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label for="tcp_test" class="ml-3 text-base font-medium text-gray-900 dark:text-white">
              TCP Handshake Test
            </label>
          </div>

          <div class="ml-8 space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                Targets
              </label>
              <div class="space-y-2">
                <div
                  v-for="(target, index) in config.tests.tcp_handshake.targets"
                  :key="index"
                  class="flex items-center gap-2"
                >
                  <input
                    v-model="config.tests.tcp_handshake.targets[index]"
                    type="text"
                    class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    @click="removeTarget('tcp_handshake', index)"
                    class="text-red-500 hover:text-red-700 hover:bg-transparent bg-transparent p-2"
                  >
                    <Trash/>
                  </button>
                </div>
              </div>
              <button
                type="button"
                @click="addTarget('tcp_handshake')"
                class="mt-2 btn text-blue-300 dark:text-blue-400 hover:text-blue-700 text-sm flex items-center gap-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
                Add Target
              </button>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                Interval (seconds)
              </label>
              <input
                v-model.number="config.tests.tcp_handshake.interval_sec"
                type="number"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <!-- Gateway Latency Test -->
        <div>
          <div class="flex items-center mb-4">
            <input
              id="gateway_test"
              v-model="config.tests.gateway_latency.enabled"
              type="checkbox"
              class="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label for="gateway_test" class="ml-3 text-base font-medium text-gray-900 dark:text-white">
              Gateway Latency Test
            </label>
          </div>

          <div class="ml-8">
            <label class="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              Interval (seconds)
            </label>
            <input
              v-model.number="config.tests.gateway_latency.interval_sec"
              type="number"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <!-- System Metrics Collection -->
        <div>
          <div class="flex items-center mb-4">
            <input
              id="system_metrics"
              v-model="config.metrics.push_enabled"
              type="checkbox"
              class="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label for="system_metrics" class="ml-3 text-base font-medium text-gray-900 dark:text-white">
              System Metrics Collection
            </label>
          </div>

          <div class="ml-8 space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                Metrics Push URL
              </label>
              <input
                v-model="config.metrics.push_url"
                type="text"
                placeholder="Server endpoint for metrics submission"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                Push Interval (seconds)
              </label>
              <input
                v-model.number="config.metrics.push_interval_sec"
                type="number"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>


        <div>
          <div class="flex items-center justify-between mb-4">
            <label class="text-base font-medium text-gray-900 dark:text-white flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
              SNMP Polling Targets
            </label>
            <button
              type="button"
              @click="addSnmpTarget"
              class="btn"
            >

              Add SNMP Target
            </button>
          </div>

          <!-- List of existing targets -->
          <div v-if="snmpTargets.length > 0" class="space-4 grid grid-cols-2">
            <div
              v-for="target in snmpTargets"
              :key="target.id"
              class="border border-gray-300 dark:border-gray-600 rounded-lg p-6 bg-white dark:bg-gray-800 m-2"
            >
              <div class="flex items-center justify-between mb-4">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-2 text-blue-600 dark:text-blue-400">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                  <h4 class="text-lg font-semibold text-gray-900 dark:text-white">
                    {{ target.name || 'Unnamed Target' }}
                  </h4>
                  <span 
                    v-if="!target.enabled"
                    class="ml-3 px-2 py-1 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded"
                  >
                    Disabled
                  </span>
                  <span 
                    v-else
                    class="ml-3 px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded"
                  >
                    Enabled
                  </span> 
                </div>
                <div class="flex items-center gap-2">
                  <button
                    type="button"
                    @click="editSnmpTargetHandler(target)"
                    class="text-blue-600 dark:text-blue-400 hover:bg-transparent bg-transparent hover:text-blue-700 p-2"
                    title="Edit"
                  >
                    <Edit/>
                  </button>
                  <button
                    type="button"
                    @click="removeSnmpTarget(target.id)"
                    class="text-red-500 hover:text-red-700 hover:bg-transparent bg-transparent p-2"
                    title="Delete"
                  >
                    <Trash/>
                  </button>
                </div>
              </div>

              <div class="flex justify-between">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Target IP:</span>
                  <span class="ml-2 text-gray-900 dark:text-white font-medium">{{ target.target_ip }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Community:</span>
                  <span class="ml-2 text-gray-900 dark:text-white font-medium">{{ target.community }}</span>
                </div>
              </div>

            </div>
          </div>
          
          <div v-else-if="!showSnmpForm" class="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mx-auto text-gray-400 mb-2">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
            <p class="text-gray-500 dark:text-gray-400">No SNMP targets configured. Click "Add SNMP Target" to get started.</p>
          </div>
        </div>
      </div>


      <div class="flex justify-end space-x-3 mt-6">
        <button
          type="button"
          @click="router.push({ name: 'DeviceView' })"
          class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-gray-500 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="btn border border-transparent shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Save Configuration
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getDeviceConfigByMac, updateDevice } from '@/services/mivulite/devicesService';
import { 
  getSnmpTargets, 
  createSnmpTarget, 
  updateSnmpTarget, 
  deleteSnmpTarget 
} from '@/services/mivulite/snmpService';
import SNMPView from './SNMPView.vue';
import { Trash, Edit } from 'lucide-vue-next';

const route = useRoute();
const router = useRouter();

const isNewDevice = ref(false);
const loading = ref(false);
const error = ref(null);

const config = ref({
  auth_token: "",
  device_id: "",
  location: "",
  name: "",
  organization_id: "",
  provisioned: false,
  site_code: "",
  site_name: "",
  mimir_org_id: "",
  mimir_url: "",
  metrics: {
    push_enabled: false,
    push_interval_sec: 60,
    push_url: ""
  },
  tests: {
    ping: {
      enabled: false,
      interval_sec: 60,
      targets: []
    },
    dns: {
      enabled: false,
      interval_sec: 60,
      targets: []
    },
    tcp_handshake: {
      enabled: false,
      interval_sec: 60,
      targets: []
    },
    gateway_latency: {
      enabled: false,
      interval_sec: 30
    },
    wifi_scan: {
      enabled: false,
      interval_sec: 300
    }
  }
});

// SNMP Targets state - separate from config
const snmpTargets = ref([]);
const editingSnmpTarget = ref(null);
const showSnmpForm = ref(false);

const fetchDevice = async (mac) => {
  try {
    loading.value = true;
    const data = await getDeviceConfigByMac(mac);
    config.value = deepMerge(config.value, data);
  } catch (err) {
    console.error(err);
    error.value = "Failed to load device configuration";
  } finally {
    loading.value = false;
  }
};

const  deepMerge = (defaults, override)  => {
  const result = { ...defaults };
  for (const key in override) {
    if (override[key] && typeof override[key] === 'object' && !Array.isArray(override[key])) {
      result[key] = deepMerge(defaults[key] ?? {}, override[key]);
    } else {
      result[key] = override[key];
    }
  }
  return result;
}

const fetchSnmpTargets = async (deviceId) => {
  try {
    const targets = await getSnmpTargets(deviceId);
    snmpTargets.value = targets;
  } catch (err) {
    console.error('Error fetching SNMP targets:', err);
  }
};

const addSnmpTarget = () => {
  editingSnmpTarget.value = {
    name: '',
    target_ip: '',
    port: 161,
    community: '',
    interface_index: 0,
    poll_interval_sec: 60,
    timeout_ms: 3000,
    walk_all_interfaces: true,
    use_64bit_counters: true,
    enabled: true,
    description: '',
    custom_oids: []
  };
  showSnmpForm.value = true;
};

const editSnmpTargetHandler = (target) => {
  editingSnmpTarget.value = { ...target };
  showSnmpForm.value = true;
};

const cancelSnmpEdit = () => {
  editingSnmpTarget.value = null;
  showSnmpForm.value = false;
};

const handleSnmpTargetSaved = async (targetData) => {
  try {
    if (targetData.id) {
      // Update existing target
      await updateSnmpTarget(targetData.id, targetData);
    } else {
      // Create new target
      await createSnmpTarget(route.params.id, targetData);
    }
    
    // Refresh the list
    await fetchSnmpTargets(route.params.id);
    
    // Close form
    cancelSnmpEdit();
  } catch (err) {
    console.error('Error saving SNMP target:', err);
    error.value = 'Failed to save SNMP target';
  }
};

const removeSnmpTarget = async (targetId) => {
  try {
    await deleteSnmpTarget(targetId);
    await fetchSnmpTargets(route.params.id);
  } catch (err) {
    console.error('Error deleting SNMP target:', err);
    error.value = 'Failed to delete SNMP target';
  }
};

const addTarget = (testType) => {
  config.value.tests[testType].targets.push("");
};

const removeTarget = (testType, index) => {
  config.value.tests[testType].targets.splice(index, 1);
};

const saveConfiguration = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    await updateDevice(route.params.id, config.value);
    router.push({ name: "DeviceView" });
  } catch (err) {
    console.error("Error saving configuration:", err);
    error.value = "An error occurred while saving the configuration.";
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  const id = route.params.id;
  
  if (id === "new") {
    isNewDevice.value = true;
  } else {
    await fetchDevice(id);
    await fetchSnmpTargets(id);
  }
});
</script>